import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useCustomer } from '../../context/CustomerContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { 
  MapPin, Plus, Edit2, Trash2, Check, X, Star, AlertCircle 
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AccountAddresses = () => {
  const { customer, getAuthHeaders, refreshProfile } = useCustomer();
  const [addresses, setAddresses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    label: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    country: 'United States',
    is_default: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (customer?.addresses) {
      setAddresses(customer.addresses);
    }
  }, [customer]);

  const resetForm = () => {
    setFormData({
      label: '',
      street: '',
      city: '',
      state: '',
      zip: '',
      country: 'United States',
      is_default: false
    });
    setShowForm(false);
    setEditingId(null);
    setError('');
  };

  const handleEdit = (address) => {
    setFormData(address);
    setEditingId(address.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const headers = getAuthHeaders();
      
      if (editingId) {
        await axios.put(`${API}/customer/addresses/${editingId}`, formData, { headers });
      } else {
        await axios.post(`${API}/customer/addresses`, formData, { headers });
      }
      
      await refreshProfile();
      resetForm();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to save address');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (addressId) => {
    if (!window.confirm('Are you sure you want to delete this address?')) return;
    
    try {
      await axios.delete(`${API}/customer/addresses/${addressId}`, {
        headers: getAuthHeaders()
      });
      await refreshProfile();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to delete address');
    }
  };

  return (
    <div className="space-y-6" data-testid="account-addresses">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-serif text-stone-800">My Addresses</h2>
        {!showForm && (
          <Button 
            onClick={() => setShowForm(true)}
            className="bg-amber-700 hover:bg-amber-800"
            data-testid="add-address-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Address
          </Button>
        )}
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2 text-red-700">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-serif">
              {editingId ? 'Edit Address' : 'Add New Address'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="label">Label</Label>
                <Input
                  id="label"
                  value={formData.label}
                  onChange={(e) => setFormData(prev => ({ ...prev, label: e.target.value }))}
                  placeholder="Home, Work, etc."
                  required
                  data-testid="address-label-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="street">Street Address</Label>
                <Input
                  id="street"
                  value={formData.street}
                  onChange={(e) => setFormData(prev => ({ ...prev, street: e.target.value }))}
                  placeholder="123 Main St, Apt 4"
                  required
                  data-testid="address-street-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                    placeholder="New York"
                    required
                    data-testid="address-city-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    value={formData.state}
                    onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                    placeholder="NY"
                    required
                    data-testid="address-state-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="zip">ZIP Code</Label>
                  <Input
                    id="zip"
                    value={formData.zip}
                    onChange={(e) => setFormData(prev => ({ ...prev, zip: e.target.value }))}
                    placeholder="10001"
                    required
                    data-testid="address-zip-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    value={formData.country}
                    onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                    placeholder="United States"
                    required
                    data-testid="address-country-input"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_default"
                  checked={formData.is_default}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_default: e.target.checked }))}
                  className="rounded border-stone-300"
                />
                <Label htmlFor="is_default" className="font-normal cursor-pointer">
                  Set as default address
                </Label>
              </div>

              <div className="flex gap-3">
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={resetForm}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-amber-700 hover:bg-amber-800"
                  disabled={submitting}
                  data-testid="save-address-btn"
                >
                  {submitting ? 'Saving...' : (editingId ? 'Update Address' : 'Add Address')}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Address List */}
      {addresses.length === 0 && !showForm ? (
        <Card>
          <CardContent className="p-12 text-center">
            <MapPin className="h-16 w-16 mx-auto mb-4 text-stone-300" />
            <h3 className="text-lg font-medium text-stone-800 mb-2">No addresses saved</h3>
            <p className="text-stone-500">Add an address to make checkout faster</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {addresses.map((address) => (
            <Card key={address.id} className={address.is_default ? 'ring-2 ring-amber-500' : ''}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-amber-700" />
                    <span className="font-semibold text-stone-800">{address.label}</span>
                  </div>
                  {address.is_default && (
                    <Badge className="bg-amber-100 text-amber-800">
                      <Star className="h-3 w-3 mr-1" /> Default
                    </Badge>
                  )}
                </div>
                
                <div className="text-stone-600 space-y-1 mb-4">
                  <p>{address.street}</p>
                  <p>{address.city}, {address.state} {address.zip}</p>
                  <p>{address.country}</p>
                </div>

                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleEdit(address)}
                  >
                    <Edit2 className="h-4 w-4 mr-1" /> Edit
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => handleDelete(address.id)}
                  >
                    <Trash2 className="h-4 w-4 mr-1" /> Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AccountAddresses;
